/**
 * Shared limits and patterns for marketing forms (request-demo, contact).
 * Keep numeric caps in sync with `website/api/contact.ts` and `website/server/contact-api.mjs`.
 */

/** Practical work-email pattern (stricter than a single @ check, not full RFC 5322). */
export const WORK_EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/** Optional phone: digits and common formatting symbols only. */
export const OPTIONAL_PHONE_PATTERN = /^[+().\d\s-]*$/;

export const LIMITS = {
  name: { min: 2, max: 100 },
  email: { max: 254 },
  jobTitle: { min: 2, max: 120 },
  company: { min: 2, max: 150 },
  /** Demo / sales forms: phone is required (min digits after trim). */
  phone: { min: 5, max: 40 },
  useCase: { min: 10, max: 8000 },
  contactMessage: { min: 10, max: 8000 },
} as const;

export function isValidWorkEmail(email: string): boolean {
  const t = email.trim();
  if (t.length < 1 || t.length > LIMITS.email.max) return false;
  return WORK_EMAIL_PATTERN.test(t);
}

export function isValidOptionalPhone(phone: string): boolean {
  const t = phone.trim();
  if (t.length === 0) return true;
  if (t.length > LIMITS.phone.max) return false;
  return OPTIONAL_PHONE_PATTERN.test(t);
}

/** Required phone for request-demo (non-empty, length, allowed characters). */
export function isValidDemoPhone(phone: string): boolean {
  const t = phone.trim();
  if (t.length < LIMITS.phone.min || t.length > LIMITS.phone.max) return false;
  return OPTIONAL_PHONE_PATTERN.test(t);
}

/** Consumer domains blocked for B2B pilot self-signup (corporate email required). */
export const FREE_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "live.com",
  "msn.com",
  "proton.me",
  "protonmail.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "ymail.com",
]);

export const CORPORATE_EMAIL_REQUIRED_MESSAGE =
  "Use your corporate or work email (your company domain). Personal providers such as Gmail, Yahoo, Hotmail, Outlook.com, iCloud, and similar are not accepted for the pilot.";

/** Shown when the address fails basic email syntax (before the consumer-domain check). */
export const WORK_EMAIL_FORMAT_MESSAGE =
  "Enter a complete work email (example: you@company.com). We cannot accept personal mailboxes like Gmail, Yahoo, Hotmail, or Outlook.com for this pilot.";
export function isCorporatePilotEmail(email: string): boolean {
  if (!isValidWorkEmail(email)) return false;
  const at = email.trim().lastIndexOf("@");
  if (at < 0) return false;
  const domain = email.slice(at + 1).toLowerCase();
  return !FREE_EMAIL_DOMAINS.has(domain);
}
